import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
    HomeIcon,
    CurrencyEuroIcon,
    Square2StackIcon,
    HomeModernIcon,
    WrenchScrewdriverIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    DocumentTextIcon
} from "@heroicons/react/24/outline";
import StepIndicator from "./StepIndicator";

const EditPropertyForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [localizaciones, setLocalizaciones] = useState([]);
    const [selectedProvincia, setSelectedProvincia] = useState("");
    const [selectedMunicipio, setSelectedMunicipio] = useState("");
    const [formData, setFormData] = useState({
        titulo: "",
        descripcion: "",
        precio: "",
        superficie: "",
        habitaciones: "",
        banos: "",
        municipio: "",
        distrito: "",
        direccion: ""
    });

    useEffect(() => {
        const fetchLocalizaciones = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/localizaciones/agrupadas`);
                const data = await res.json();
                setLocalizaciones(data);
            } catch (err) {
                console.error("Error cargando localizaciones:", err);
            }
        };

        fetchLocalizaciones();
    }, []);

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${id}`, {
                    credentials: "include"
                });
                if (!res.ok) {
                    throw new Error('Error fetching property');
                }
                const data = await res.json();
                setFormData({
                    titulo: data.titulo || "",
                    descripcion: data.descripcion || "",
                    precio: data.precio || "",
                    superficie: data.superficie || "",
                    habitaciones: data.habitaciones || "",
                    banos: data.banos || "",
                    municipio: data.municipio || "",
                    distrito: data.distrito || "",
                    direccion: data.direccion || ""
                });

                // Set the selected provincia and municipio based on the property data
                const provincia = localizaciones.find(p => 
                    p.municipios.some(m => m.municipio === data.municipio)
                )?.provincia;
                if (provincia) {
                    setSelectedProvincia(provincia);
                    setSelectedMunicipio(data.municipio);
                }
            } catch (err) {
                console.error("Error fetching property:", err);
                toast.error("Error al cargar la propiedad");
                navigate("/mis-propiedades");
            } finally {
                setLoading(false);
            }
        };

        if (localizaciones.length > 0) {
            fetchProperty();
        }
    }, [id, navigate, localizaciones]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleProvinciaChange = (e) => {
        const provincia = e.target.value;
        setSelectedProvincia(provincia);
        setSelectedMunicipio("");
        setFormData(prev => ({
            ...prev,
            municipio: "",
            distrito: ""
        }));
    };

    const handleMunicipioChange = (e) => {
        const municipio = e.target.value;
        setSelectedMunicipio(municipio);
        
        // Get the locations for the selected municipality
        const provinciaData = localizaciones.find(p => p.provincia === selectedProvincia);
        const municipioData = provinciaData?.municipios.find(m => m.municipio === municipio);
            
        // If there's only one location option, automatically select it
        if (municipioData && municipioData.localizaciones.length === 1) {
            // For single-location municipalities, use the municipality name as the district
            setFormData(prev => ({
                ...prev,
                municipio: municipio,
                distrito: municipio // Use municipality name when district is null
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                municipio: municipio,
                distrito: ""
            }));
        }
    };

    // Add useEffect to handle automatic district selection
    useEffect(() => {
        if (selectedMunicipio && localizaciones.length > 0) {
            const provinciaData = localizaciones.find(p => p.provincia === selectedProvincia);
            const municipioData = provinciaData?.municipios.find(m => m.municipio === selectedMunicipio);
            
            if (municipioData && municipioData.localizaciones.length === 1) {
                // For single-location municipalities, use the municipality name as the district
                setFormData(prev => ({
                    ...prev,
                    municipio: selectedMunicipio,
                    distrito: selectedMunicipio // Use municipality name when district is null
                }));
            }
        }
    }, [selectedMunicipio, selectedProvincia, localizaciones]);

    const handleDistritoChange = (e) => {
        const distrito = e.target.value;
        setFormData(prev => ({
            ...prev,
            distrito: distrito
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error('Error al actualizar la propiedad');

            // Navigate to the image upload page
            navigate(`/propiedades/${id}/editar/imagenes`);
        } catch (err) {
            setError('Error al actualizar la propiedad. Por favor, inténtalo de nuevo.');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const InputWithIcon = useCallback(({ icon: Icon, label, ...props }) => (
        <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
            </label>
            <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    {...props}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-lg transition duration-150 ease-in-out"
                />
            </div>
        </div>
    ), []);

    const SelectWithIcon = useCallback(({ icon: Icon, label, children, ...props }) => (
        <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
            </label>
            <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon className="h-5 w-5 text-gray-400" />
                </div>
                <select
                    {...props}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-lg transition duration-150 ease-in-out"
                >
                    {children}
                </select>
            </div>
        </div>
    ), []);

    if (loading) {
        return <div className="container mx-auto p-6">Cargando...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
            <StepIndicator currentStep={1} totalSteps={2} />
            <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Editar Propiedad</h1>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 gap-8">
                    <InputWithIcon
                        icon={HomeIcon}
                        label="Título"
                        type="text"
                        name="titulo"
                        value={formData.titulo}
                        onChange={handleInputChange}
                        required
                        placeholder="Ej: Apartamento en el centro"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <InputWithIcon
                            icon={CurrencyEuroIcon}
                            label="Precio (€)"
                            type="number"
                            name="precio"
                            value={formData.precio}
                            onChange={handleInputChange}
                            required
                            min="0"
                            step="0.01"
                            placeholder="Ej: 250000"
                        />

                        <InputWithIcon
                            icon={Square2StackIcon}
                            label="Superficie (m²)"
                            type="number"
                            name="superficie"
                            value={formData.superficie}
                            onChange={handleInputChange}
                            required
                            min="0"
                            placeholder="Ej: 85"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <InputWithIcon
                            icon={HomeModernIcon}
                            label="Habitaciones"
                            type="number"
                            name="habitaciones"
                            value={formData.habitaciones}
                            onChange={handleInputChange}
                            required
                            min="0"
                            placeholder="Ej: 3"
                        />

                        <InputWithIcon
                            icon={WrenchScrewdriverIcon}
                            label="Baños"
                            type="number"
                            name="banos"
                            value={formData.banos}
                            onChange={handleInputChange}
                            required
                            min="0"
                            placeholder="Ej: 2"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <SelectWithIcon
                            icon={BuildingOfficeIcon}
                            label="Provincia"
                            value={selectedProvincia}
                            onChange={handleProvinciaChange}
                            required
                        >
                            <option value="">Selecciona una provincia</option>
                            {localizaciones.map(({ provincia }) => (
                                <option key={provincia} value={provincia}>
                                    {provincia}
                                </option>
                            ))}
                        </SelectWithIcon>

                        <SelectWithIcon
                            icon={BuildingOfficeIcon}
                            label="Municipio"
                            value={selectedMunicipio}
                            onChange={handleMunicipioChange}
                            required
                            disabled={!selectedProvincia}
                        >
                            <option value="">Selecciona un municipio</option>
                            {selectedProvincia &&
                                localizaciones
                                    .find(p => p.provincia === selectedProvincia)
                                    ?.municipios.map(({ municipio }) => (
                                        <option key={municipio} value={municipio}>
                                            {municipio}
                                        </option>
                                    ))}
                        </SelectWithIcon>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <SelectWithIcon
                            icon={MapPinIcon}
                            label="Distrito"
                            name="distrito"
                            value={formData.distrito}
                            onChange={handleDistritoChange}
                            required
                            disabled={!selectedMunicipio}
                        >
                            <option value="">Selecciona un distrito</option>
                            {selectedMunicipio &&
                                localizaciones
                                    .find(p => p.provincia === selectedProvincia)
                                    ?.municipios.find(m => m.municipio === selectedMunicipio)
                                    ?.localizaciones.map(({ id, distrito }) => (
                                        <option key={id} value={distrito || selectedMunicipio}>
                                            {distrito || selectedMunicipio}
                                        </option>
                                    ))}
                        </SelectWithIcon>

                        <InputWithIcon
                            icon={MapPinIcon}
                            label="Dirección"
                            type="text"
                            name="direccion"
                            value={formData.direccion}
                            onChange={handleInputChange}
                            required
                            placeholder="Ej: Calle Mayor, 123"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Descripción
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute top-3 left-3 pointer-events-none">
                                <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <textarea
                                name="descripcion"
                                value={formData.descripcion}
                                onChange={handleInputChange}
                                required
                                rows="6"
                                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-lg transition duration-150 ease-in-out"
                                placeholder="Describe tu propiedad..."
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-6">
                    <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition duration-150 ease-in-out"
                    >
                        {loading ? "Guardando..." : "Siguiente: Subir imágenes"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditPropertyForm; 